import { useEffect, useMemo, useRef, useState } from "react";
import type { Attachment } from "@onemileauto/shared-types";
import { sanitizeEmailHtml } from "../../lib/sanitize-html";

interface SafeHtmlFrameProps {
  html: string;
  attachments: Attachment[];
  mailboxId: string;
}

/**
 * Renders inbound message HTML inside `sandbox="allow-same-origin"` with no
 * `allow-scripts` token — the sandbox is the real security backstop (see
 * lib/sanitize-html.ts for why), DOMPurify sanitization is the other,
 * independent layer. Do not add `allow-scripts` here.
 *
 * `allow-same-origin` (without allow-scripts) is what lets this component
 * read `contentDocument` to auto-size the iframe's height to its content —
 * that's safe specifically because scripts still can't run inside it.
 */
export function SafeHtmlFrame({ html, attachments, mailboxId }: SafeHtmlFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(120);

  const doc = useMemo(() => sanitizeEmailHtml(html, attachments, mailboxId), [html, attachments, mailboxId]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
    },
    [],
  );

  const handleLoad = () => {
    const body = iframeRef.current?.contentDocument?.body;
    if (!body) return;

    resizeObserverRef.current?.disconnect();

    const update = () => setHeight(Math.max(80, body.scrollHeight + 24));
    update();

    const observer = new ResizeObserver(update);
    observer.observe(body);
    resizeObserverRef.current = observer;
  };

  return (
    <iframe
      ref={iframeRef}
      title="Message content"
      sandbox="allow-same-origin"
      srcDoc={doc}
      onLoad={handleLoad}
      style={{ width: "100%", height, border: "none", display: "block", colorScheme: "light" }}
    />
  );
}
